import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  CircularProgress,
  Alert,
  Stack,
  FormControl,
  InputLabel,
  Select,
  Drawer,
  useMediaQuery,
  useTheme,
  Paper,
  Divider,
  InputAdornment
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  CheckCircle as CheckIcon,
  Pets as PetsIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  Search as SearchIcon,
  Save as SaveIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import ro from 'date-fns/locale/ro';
import { format, startOfWeek, endOfWeek, addDays, isSameDay } from 'date-fns';
import { api } from '@services/authService';
import { formatForDisplay } from '@utils/dateFormatter';
import FullCalendarWeekView from '@components/shared/FullCalendarWeekView';

interface Appointment {
  id: string;
  scheduledAt: string;
  status: string;
  reason: string;
  duration: number;
  emergency: boolean;
  patient?: any;
  owner?: any;
}

const DoctorAppointments: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [loading, setLoading] = useState(true);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [view, setView] = useState<'day' | 'week' | 'list'>('week');
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
  const [newStatus, setNewStatus] = useState('');
  const [statusNotes, setStatusNotes] = useState('');
  
  // Filter states
  const [statusFilter, setStatusFilter] = useState('all');
  const [speciesFilter, setSpeciesFilter] = useState('all');
  const [serviceFilter, setServiceFilter] = useState('all');
  const [filtersDrawerOpen, setFiltersDrawerOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(true);

  // Get unique species and services
  const uniqueSpecies = Array.from(new Set(
    appointments
      .map(apt => apt.patient?.species)
      .filter(Boolean)
  )).sort();

  const uniqueServices = Array.from(new Set(
    appointments
      .map(apt => (apt as any).service?.name || (apt as any).serviceName)
      .filter(Boolean)
  )).sort();

  // Filtered appointments
  const filteredAppointments = appointments.filter(apt => {
    if (statusFilter !== 'all' && apt.status !== statusFilter) return false;
    if (speciesFilter !== 'all' && apt.patient?.species !== speciesFilter) return false;
    if (serviceFilter !== 'all') {
      const serviceName = (apt as any).service?.name || (apt as any).serviceName;
      if (serviceName !== serviceFilter) return false;
    }
    return true;
  });

  // Count active filters
  const activeFiltersCount = [statusFilter, speciesFilter, serviceFilter].filter(f => f !== 'all').length;

  useEffect(() => {
    loadAppointments();
  }, [selectedDate, view]);

  const loadAppointments = async () => {
    try {
      setLoading(true);
      
      let from, to;
      if (view === 'day') {
        from = format(selectedDate, 'yyyy-MM-dd');
        to = format(selectedDate, 'yyyy-MM-dd');
      } else if (view === 'week') {
        from = format(startOfWeek(selectedDate, { weekStartsOn: 1 }), 'yyyy-MM-dd');
        to = format(endOfWeek(selectedDate, { weekStartsOn: 1 }), 'yyyy-MM-dd');
      } else {
        // Lista - urmatoarele 30 de zile
        from = format(selectedDate, 'yyyy-MM-dd');
        to = format(addDays(selectedDate, 30), 'yyyy-MM-dd');
      }

      const response = await api.get(`/doctor/appointments?from=${from}&to=${to}`);
      
      if (response.data.data && response.data.data.length > 0) {
      } else {
        console.warn('⚠️ No appointments found in the response!');
      }
      
      setAppointments(response.data.data || []);
    } catch (error) {
      console.error('Eroare la incarcarea programarilor:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async () => {
    if (!selectedAppointment || !newStatus) return;

    try {
      await api.patch(`/doctor/appointments/${selectedAppointment.id}/status`, {
        status: newStatus,
        notes: statusNotes
      });

      setStatusDialogOpen(false);
      setSelectedAppointment(null);
      setNewStatus('');
      setStatusNotes('');
      loadAppointments();
    } catch (error) {
      console.error('Eroare la actualizarea statusului:', error);
    }
  };

  const openStatusDialog = (appointment: Appointment) => {
    setSelectedAppointment(appointment);
    setNewStatus(appointment.status);
    setStatusDialogOpen(true);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'scheduled': return 'info';
      case 'confirmed': return 'success';
      case 'in_progress': return 'warning';
      case 'completed': return 'success';
      case 'no_show': return 'error';
      case 'cancelled': return 'default';
      default: return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    const labels: any = {
      scheduled: 'Programat',
      confirmed: 'Confirmat',
      in_progress: 'In curs',
      completed: 'Finalizat',
      no_show: 'Absent',
      cancelled: 'Anulat'
    };
    return labels[status] || status;
  };

  const renderDayView = () => {
    const dayAppointments = appointments.filter(apt =>
      isSameDay(new Date(apt.scheduledAt), selectedDate)
    ).sort((a, b) => 
      new Date(a.scheduledAt).getTime() - new Date(b.scheduledAt).getTime()
    );

    if (dayAppointments.length === 0) {
      return (
        <Alert severity="info" sx={{ mt: 2 }}>
          Nu exista programari pentru aceasta zi.
        </Alert>
      );
    }

    return (
      <List>
        {dayAppointments.map((appointment) => (
          <Card key={appointment.id} sx={{ mb: 2 }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box display="flex" alignItems="center" gap={2}>
                  <Avatar sx={{ bgcolor: appointment.emergency ? 'error.main' : 'primary.main' }}>
                    <PetsIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h6">
                      {new Date(appointment.scheduledAt).toLocaleTimeString('ro-RO', { 
                        hour: '2-digit', 
                        minute: '2-digit' 
                      })}
                      {appointment.emergency && (
                        <Chip label="URGENT" color="error" size="small" sx={{ ml: 1 }} />
                      )}
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {appointment.patient?.name || 'Pacient necunoscut'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Proprietar: {appointment.owner?.firstName} {appointment.owner?.lastName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Motiv: {appointment.reason}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Durata: {appointment.duration} minute
                    </Typography>
                  </Box>
                </Box>
                <Box display="flex" flexDirection="column" gap={1} alignItems="flex-end">
                  <Chip
                    label={getStatusLabel(appointment.status)}
                    color={getStatusColor(appointment.status)}
                    size="small"
                  />
                  <Stack direction="row" spacing={1}>
                    {appointment.status === 'scheduled' && (
                      <Button
                        size="small"
                        variant="outlined"
                        color="success"
                        startIcon={<CheckIcon />}
                        onClick={() => {
                          setSelectedAppointment(appointment);
                          setNewStatus('in_progress');
                          handleStatusChange();
                        }}
                      >
                        Check-in
                      </Button>
                    )}
                    {appointment.status === 'in_progress' && (
                      <Button
                        size="small"
                        variant="contained"
                        color="success"
                        startIcon={<CheckIcon />}
                        onClick={() => {
                          setSelectedAppointment(appointment);
                          setNewStatus('completed');
                          handleStatusChange();
                        }}
                      >
                        Finalizeaza
                      </Button>
                    )}
                    <IconButton
                      size="small"
                      onClick={() => openStatusDialog(appointment)}
                    >
                      <EditIcon />
                    </IconButton>
                  </Stack>
                </Box>
              </Box>
            </CardContent>
          </Card>
        ))}
      </List>
    );
  };

  const renderWeekView = () => {
    // Transform appointments to match FullCalendarWeekView format
    const transformedAppointments = filteredAppointments.map(apt => {
      // helper: format age if birthDate exists
      const computeAge = (birthDate?: string) => {
        if (!birthDate) return undefined;
        try {
          const b = new Date(birthDate);
          const now = new Date();
          let years = now.getFullYear() - b.getFullYear();
          let months = now.getMonth() - b.getMonth();
          if (months < 0) {
            years -= 1;
            months += 12;
          }
          if (years <= 0 && months > 0) return `${months} luni`;
          if (years > 0 && months > 0) return `${years} ani ${months} luni`;
          if (years > 0) return `${years} ani`;
          return undefined;
        } catch {
          return undefined;
        }
      };

      const startDate = new Date(apt.scheduledAt);
      const transformed = {
        id: apt.id,
        date: format(startDate, 'yyyy-MM-dd'),
        time: format(startDate, 'HH:mm'),
        duration: apt.duration || 30,
        patientName: apt.patient?.name || 'Necunoscut',
        clientName: `${apt.owner?.firstName || ''} ${apt.owner?.lastName || ''}`.trim() || 'Necunoscut',
        serviceName: apt.reason || 'Consultație',
        animalSpecies: apt.patient?.species || 'Necunoscut',
        status: apt.status,
        veterinarianName: (apt as any).doctor?.fullName || 'Dr. Curent',
        veterinarianId: (apt as any).doctorId || 'current',
        priority: apt.emergency ? 'high' : 'normal',

        // Rich context for details dialog
        clientPhone: (apt as any).owner?.phone || (apt as any).owner?.phoneNumber || '',
        clientEmail: (apt as any).owner?.email || '',
        clientId: (apt as any).owner?.id,
        patientId: (apt as any).patient?.id,
        locationName: (apt as any).location?.name || (apt as any).clinic?.name,
        roomName: (apt as any).room?.name,
        reason: apt.reason,
        notes: (apt as any).notes || '',
        createdAt: (apt as any).createdAt,
        updatedAt: (apt as any).updatedAt,

        // Patient attributes (optional)
        animalBreed: (apt as any).patient?.breed,
        animalGender: (apt as any).patient?.gender,
        animalWeight: (apt as any).patient?.weight,
        animalAge: computeAge((apt as any).patient?.birthDate),
        allergies: (apt as any).patient?.allergies || [],
        flags: (apt as any).patient?.flags || (apt as any).patient?.warnings || []
      };
      return transformed;
    });

    if (filteredAppointments.length === 0) {
    return (
        <Box>
          <Alert severity="info" sx={{ mb: 2 }}>
            {appointments.length === 0 
              ? 'Nu există programări în această perioadă. Creați o programare nouă pentru a o vedea în calendar.'
              : 'Nu există programări pentru filtrele selectate. Resetați filtrele sau schimbați criteriile de căutare.'
            }
          </Alert>
          <FullCalendarWeekView
            appointments={[]}
            selectedDate={selectedDate}
            onDateChange={setSelectedDate}
            onAppointmentClick={(apt: any) => {
              const original = appointments.find(a => a.id === apt.id);
              if (original) openStatusDialog(original);
            }}
            onAddAppointment={() => {
              // Add appointment logic here
            }}
            onAppointmentUpdate={async (data: any) => {
              // Update appointment logic here
              await loadAppointments();
            }}
            veterinarians={[]}
          />
        </Box>
      );
    }

    const handleCalendarUpdate = async (data: any) => {
      try {
        if (!data?.id) return;

        const current = appointments.find(a => a.id === data.id);
        const normalizeStatus = (s?: string) => {
          if (!s) return undefined;
          switch (s) {
            case 'arrived':
            case 'present':
              return 'in_progress';
            case 'in-progress':
              return 'in_progress';
            case 'no-show':
              return 'no_show';
            case 'rescheduled':
              return undefined; // nu trimitem patch status pentru "reprogramat"
            default:
              return s;
          }
        };

        const nextStatus = normalizeStatus(data.status);
        if (nextStatus && nextStatus !== current?.status) {
          await api.patch(`/doctor/appointments/${data.id}/status`, {
            status: nextStatus,
            notes: data.notes || ''
          });
        }

        // Update programare (data/ora/durata)
        if (data.date && data.time) {
          const scheduledAt = `${data.date}T${String(data.time).padStart(5, '0')}:00`;
          await api.patch(`/doctor/appointments/${data.id}` as string, {
            scheduledAt,
            duration: data.duration
          });
        }

        // Mutare la alt medic
        if (data.veterinarianId) {
          await api.patch(`/doctor/appointments/${data.id}` as string, {
            doctorId: data.veterinarianId
          });
        }
      } catch (e) {
        console.error('Eroare la actualizarea programarii din calendar:', e);
      } finally {
        await loadAppointments();
      }
    };

          return (
                  <Box>
        <FullCalendarWeekView
          appointments={transformedAppointments as any}
          selectedDate={selectedDate}
          onDateChange={setSelectedDate}
          onAppointmentClick={(apt: any) => {
            const original = appointments.find(a => a.id === apt.id);
            if (original) openStatusDialog(original);
          }}
          onAddAppointment={() => {
            // Add appointment logic here
          }}
          onAppointmentUpdate={handleCalendarUpdate}
          veterinarians={[]}
        />
                  </Box>
    );
  };

  const renderListView = () => {
    const groupedByDate = appointments.reduce((acc: any, apt) => {
      const dateKey = format(new Date(apt.scheduledAt), 'yyyy-MM-dd');
      if (!acc[dateKey]) acc[dateKey] = [];
      acc[dateKey].push(apt);
      return acc;
    }, {});

    return (
      <Box sx={{ mt: 2 }}>
        {Object.keys(groupedByDate).sort().map(dateKey => (
          <Box key={dateKey} sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              {format(new Date(dateKey), 'EEEE, d MMMM yyyy', { locale: ro })}
            </Typography>
            <List>
              {groupedByDate[dateKey].map((appointment: Appointment) => (
                <ListItem key={appointment.id} sx={{ bgcolor: 'background.paper', mb: 1, borderRadius: 1 }}>
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: appointment.emergency ? 'error.main' : 'primary.main' }}>
                      <PetsIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography>
                          {new Date(appointment.scheduledAt).toLocaleTimeString('ro-RO', { 
                            hour: '2-digit', 
                            minute: '2-digit' 
                          })}
                        </Typography>
                        <Typography fontWeight={600}>
                          {appointment.patient?.name}
                        </Typography>
                        {appointment.emergency && (
                          <Chip label="URGENT" color="error" size="small" />
                        )}
                      </Box>
                    }
                    secondary={
                      <>
                        Proprietar: {appointment.owner?.firstName} {appointment.owner?.lastName} • {appointment.reason}
                      </>
                    }
                  />
                  <Chip
                    label={getStatusLabel(appointment.status)}
                    color={getStatusColor(appointment.status)}
                    size="small"
                  />
                  <IconButton onClick={() => openStatusDialog(appointment)}>
                    <EditIcon />
                  </IconButton>
                </ListItem>
              ))}
            </List>
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ro}>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" gutterBottom>
            Programările mele
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Vizualizare și gestionare programări
          </Typography>
        </Box>

        {/* Time Navigation Bar */}
        <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
          <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" flexWrap="wrap">
            {/* Left: Date Navigation */}
            <Stack direction="row" spacing={2} alignItems="center">
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  const newDate = new Date(selectedDate);
                  newDate.setDate(newDate.getDate() - 7);
                  setSelectedDate(newDate);
                }}
              >
                ←
              </Button>
              
              <Button
                size="small"
                variant="outlined"
                onClick={() => setSelectedDate(new Date())}
              >
                Astăzi
              </Button>
              
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  const newDate = new Date(selectedDate);
                  newDate.setDate(newDate.getDate() + 7);
                  setSelectedDate(newDate);
                }}
              >
                →
              </Button>
              
                <DatePicker
                  value={selectedDate}
                  onChange={(newValue) => newValue && setSelectedDate(newValue)}
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: { width: 150 }
                  }
                }}
              />
            </Stack>

            {/* Center: View Toggle */}
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                variant={view === 'day' ? 'contained' : 'outlined'}
                onClick={() => setView('day')}
                sx={{ minWidth: 60 }}
              >
                Zi
              </Button>
              <Button
                size="small"
                variant={view === 'week' ? 'contained' : 'outlined'}
                onClick={() => setView('week')}
                sx={{ minWidth: 90 }}
              >
                Săptămână
              </Button>
              <Button
                size="small"
                variant={view === 'list' ? 'contained' : 'outlined'}
                onClick={() => setView('list')}
                sx={{ minWidth: 60 }}
              >
                Listă
              </Button>
            </Stack>

            {/* Right: New Appointment */}
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                window.location.assign('/doctor/patients');
              }}
            >
              Programare nouă
            </Button>
          </Stack>
        </Paper>

        {/* Filters Toggle */}
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Button
            variant="text"
            onClick={() => setShowFilters(!showFilters)}
            sx={{ 
              textTransform: 'none',
              color: 'text.secondary',
              '&:hover': { backgroundColor: 'transparent' }
            }}
          >
            {showFilters ? 'Ascunde filtre' : 'Afișează filtre'}
          </Button>
        </Box>

        {/* Sticky Filters & Legend */}
        {showFilters && (
          <Paper 
            sx={{ 
              p: 2, 
              mb: 2, 
              borderRadius: 2,
              position: 'sticky',
              top: theme.spacing(8),
              zIndex: theme.zIndex.appBar + 1,
              backgroundColor: 'white',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}
          >
          {isMobile ? (
            /* Mobile: Show Filters Button */
            <Button
              variant="outlined"
              startIcon={<FilterIcon />}
              onClick={() => setFiltersDrawerOpen(true)}
              fullWidth
            >
              Filtre {activeFiltersCount > 0 && `(${activeFiltersCount})`}
            </Button>
          ) : (
            /* Desktop: Show Filters */
            <Stack spacing={2}>
              {/* Search Bar */}
              <TextField
                size="small"
                placeholder="Caută programări..."
                sx={{ minWidth: 200 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
              
              {/* Filters Row */}
              <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                {/* Status Chips */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography variant="body2" sx={{ fontWeight: 500, color: '#555', mr: 1 }}>
                    Status:
                  </Typography>
                  {[
                    { value: 'scheduled', label: 'Neconfirmat', color: '#9e9e9e' },
                    { value: 'confirmed', label: 'Confirmat', color: '#4caf50' },
                    { value: 'rescheduled', label: 'Reprogramat', color: '#ff9800' },
                    { value: 'present', label: 'Check-in', color: '#2196f3' },
                    { value: 'completed', label: 'Finalizat', color: '#9c27b0' },
                    { value: 'cancelled', label: 'Anulat', color: '#e74c3c' },
                    { value: 'no_show', label: 'No-show', color: '#ffb6b6' }
                  ].map((status) => (
                    <Chip
                      key={status.value}
                      label={status.label}
                      size="small"
                      clickable
                      variant={statusFilter === status.value ? 'filled' : 'outlined'}
                      onClick={() => setStatusFilter(statusFilter === status.value ? 'all' : status.value)}
                      sx={{
                        backgroundColor: statusFilter === status.value ? status.color : 'transparent',
                        color: statusFilter === status.value ? 'white' : status.color,
                        borderColor: status.color,
                        fontWeight: 500,
                        fontSize: '12px',
                        '&:hover': {
                          backgroundColor: statusFilter === status.value ? status.color : `${status.color}20`
                        }
                      }}
                    />
                  ))}
                </Box>

                {/* Species Filter */}
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Specie</InputLabel>
                  <Select
                    value={speciesFilter}
                    onChange={(e) => setSpeciesFilter(e.target.value)}
                    label="Specie"
                  >
                    <MenuItem value="all">Toate speciile</MenuItem>
                    {uniqueSpecies.map(species => (
                      <MenuItem key={species} value={species}>{species}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Service Filter */}
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Serviciu</InputLabel>
                  <Select
                    value={serviceFilter}
                    onChange={(e) => setServiceFilter(e.target.value)}
                    label="Serviciu"
                  >
                    <MenuItem value="all">Toate serviciile</MenuItem>
                    {uniqueServices.map(service => (
                      <MenuItem key={service} value={service}>{service}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Action Buttons */}
                <Stack direction="row" spacing={1}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={() => {
                      setStatusFilter('all');
                      setSpeciesFilter('all');
                      setServiceFilter('all');
                    }}
                  >
                    Reset
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={() => {
                      // Filtrele sunt deja active in UI; salvarea persistenta nu este disponibila.
                    }}
                  >
                    Salvează
                  </Button>
                </Stack>
              </Stack>

            </Stack>
          )}
          </Paper>
        )}

        {/* Mobile Filters Drawer */}
        <Drawer
          anchor="left"
          open={filtersDrawerOpen}
          onClose={() => setFiltersDrawerOpen(false)}
        >
          <Box sx={{ width: 300, p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Filtre
            </Typography>
            <Stack spacing={2}>
              {/* Status Filter */}
              <FormControl size="small" fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label="Status"
                >
                  <MenuItem value="all">Toate</MenuItem>
                  <MenuItem value="scheduled">Neconfirmat</MenuItem>
                  <MenuItem value="confirmed">Confirmat</MenuItem>
                  <MenuItem value="rescheduled">Reprogramat</MenuItem>
                  <MenuItem value="present">Check-in</MenuItem>
                  <MenuItem value="completed">Finalizat</MenuItem>
                  <MenuItem value="cancelled">Anulat</MenuItem>
                  <MenuItem value="no_show">No-show</MenuItem>
                </Select>
              </FormControl>

              {/* Species Filter */}
              <FormControl size="small" fullWidth>
                <InputLabel>Specie</InputLabel>
                <Select
                  value={speciesFilter}
                  onChange={(e) => setSpeciesFilter(e.target.value)}
                  label="Specie"
                >
                  <MenuItem value="all">Toate speciile</MenuItem>
                  {uniqueSpecies.map(species => (
                    <MenuItem key={species} value={species}>{species}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Service Filter */}
              <FormControl size="small" fullWidth>
                <InputLabel>Serviciu</InputLabel>
                <Select
                  value={serviceFilter}
                  onChange={(e) => setServiceFilter(e.target.value)}
                  label="Serviciu"
                >
                  <MenuItem value="all">Toate serviciile</MenuItem>
                  {uniqueServices.map(service => (
                    <MenuItem key={service} value={service}>{service}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Divider />

              {/* Status Legend */}
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 500, color: '#555', mb: 1 }}>
                  Legendă statusuri:
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {[
                    { value: 'scheduled', label: 'Neconfirmat', color: '#9e9e9e' },
                    { value: 'confirmed', label: 'Confirmat', color: '#4caf50' },
                    { value: 'rescheduled', label: 'Reprogramat', color: '#ff9800' },
                    { value: 'present', label: 'Check-in', color: '#2196f3' },
                    { value: 'completed', label: 'Finalizat', color: '#9c27b0' },
                    { value: 'cancelled', label: 'Anulat', color: '#e74c3c' },
                    { value: 'no_show', label: 'No-show', color: '#ffb6b6' }
                  ].map((status) => (
                    <Chip
                      key={status.value}
                      label={status.label}
                      size="small"
                      sx={{
                        backgroundColor: status.color,
                        color: 'white',
                        fontWeight: 500,
                        fontSize: '12px',
                        justifyContent: 'flex-start'
                      }}
                    />
                  ))}
                </Box>
              </Box>

              <Divider />

              {/* Actions */}
              <Stack direction="row" spacing={1}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={() => {
                    setStatusFilter('all');
                    setSpeciesFilter('all');
                    setServiceFilter('all');
                  }}
                  fullWidth
                >
                  Reset
                </Button>
              </Stack>

              <Button
                variant="text"
                onClick={() => setFiltersDrawerOpen(false)}
                fullWidth
              >
                Închide
              </Button>
            </Stack>
          </Box>
        </Drawer>

        {/* Content */}
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
            <CircularProgress />
          </Box>
        ) : (
          <>
            {view === 'week' && renderWeekView()}
          </>
        )}

        {/* Status Dialog */}
        <Dialog open={statusDialogOpen} onClose={() => setStatusDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Actualizare Status Programare</DialogTitle>
          <DialogContent>
            {selectedAppointment && (
              <Box sx={{ pt: 2 }}>
                <Typography variant="body1" gutterBottom>
                  <strong>Pacient:</strong> {selectedAppointment.patient?.name}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  <strong>Data:</strong> {formatForDisplay(selectedAppointment.scheduledAt)}
                </Typography>
                <Typography variant="body2" gutterBottom sx={{ mb: 2 }}>
                  <strong>Motiv:</strong> {selectedAppointment.reason}
                </Typography>

                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={newStatus}
                    onChange={(e) => setNewStatus(e.target.value)}
                    label="Status"
                  >
                    <MenuItem value="scheduled">Programat</MenuItem>
                    <MenuItem value="confirmed">Confirmat</MenuItem>
                    <MenuItem value="in_progress">In curs</MenuItem>
                    <MenuItem value="completed">Finalizat</MenuItem>
                    <MenuItem value="no_show">Absent</MenuItem>
                    <MenuItem value="cancelled">Anulat</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  label="Note"
                  multiline
                  rows={3}
                  value={statusNotes}
                  onChange={(e) => setStatusNotes(e.target.value)}
                  placeholder="Adauga note despre programare..."
                />
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setStatusDialogOpen(false)}>Anuleaza</Button>
            <Button variant="contained" onClick={handleStatusChange}>
              Salveaza
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default DoctorAppointments;
